import { motion } from "framer-motion";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip,
  RadarChart, PolarGrid, PolarAngleAxis, Radar, PieChart, Pie, Cell, Legend,
} from "recharts";

export const VIZ = {
  EXALTED: "#FBBF24", STRONG: "#FBBF24", NORMAL: "#22D3EE", AFFLICTED: "#F59E0B",
  WEAK: "#F87171", MALEFIC: "#EF4444", DEPLETED: "#B91C1C", INACTIVE: "#64748B", MISSING: "#334155",
};
export const vizColor = (s) => VIZ[String(s || "").toUpperCase()] || "#22D3EE";
export const scoreColor = (v) => (v >= 70 ? "#FBBF24" : v >= 50 ? "#22D3EE" : v >= 20 ? "#F59E0B" : "#EF4444");

const TT = { background: "rgba(12,16,23,0.92)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, color: "#F8FAFC", fontSize: 12, backdropFilter: "blur(8px)" };

export function Panel({ title, children, className = "", testid }) {
  return (
    <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      transition={{ duration: 0.5 }} data-testid={testid}
      className={`rounded-2xl border border-white/[0.06] bg-[#0C1017] p-6 transition-[border-color,transform] duration-300 hover:-translate-y-0.5 hover:border-white/10 ${className}`}>
      {title && <p className="text-[11px] uppercase tracking-[0.22em] text-slate-400 mb-4 font-medium" style={{ fontFamily: "Manrope, sans-serif" }}>{title}</p>}
      {children}
    </motion.div>
  );
}

export function StatusPill({ status, score }) {
  const c = vizColor(status);
  return (
    <span className="text-[10px] px-2.5 py-1 rounded-full font-medium" style={{ background: c + "1a", color: c, border: `1px solid ${c}33` }}>
      {status}{score != null ? ` · ${score}%` : ""}
    </span>
  );
}

export function ScoreRing({ value, label = "score", size = 190 }) {
  const v = Math.max(0, Math.min(100, Math.round(value || 0)));
  const col = scoreColor(v);
  const r = size / 2 - 14;
  const circ = 2 * Math.PI * r;
  return (
    <div className="relative" style={{ width: size, height: size }} data-testid="viz-score-ring">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="12" />
        <motion.circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={col} strokeWidth="12" strokeLinecap="round"
          strokeDasharray={circ} initial={{ strokeDashoffset: circ }} animate={{ strokeDashoffset: circ * (1 - v / 100) }}
          transition={{ duration: 1.1, ease: "easeOut" }} style={{ filter: `drop-shadow(0 0 8px ${col}88)` }} />
      </svg>
      <div className="absolute inset-0 grid place-items-center">
        <div className="text-center">
          <span className="text-5xl font-bold" style={{ color: col, fontFamily: "Outfit, sans-serif", filter: `drop-shadow(0 0 12px ${col}66)` }}>{v}</span>
          <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-1">{label} / 100</span>
        </div>
      </div>
    </div>
  );
}

export function Spectrum({ data, height = 220, color = "#22D3EE" }) {
  return (
    <div data-testid="viz-spectrum">
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={data} margin={{ left: -12, right: 8, top: 8 }}>
          <defs>
            <linearGradient id="spec" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.55} />
              <stop offset="100%" stopColor={color} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <XAxis dataKey="name" tick={{ fill: "#64748B", fontSize: 10, fontFamily: "JetBrains Mono, monospace" }} axisLine={false} tickLine={false} interval={0} angle={data.length > 9 ? -35 : 0} textAnchor={data.length > 9 ? "end" : "middle"} height={data.length > 9 ? 54 : 24} />
          <YAxis domain={[0, 100]} tick={{ fill: "#64748B", fontSize: 10 }} axisLine={false} tickLine={false} />
          <Tooltip contentStyle={TT} cursor={{ stroke: color, strokeOpacity: 0.3 }} />
          <Area type="monotone" dataKey="value" stroke={color} strokeWidth={2.5} fill="url(#spec)"
            dot={{ r: 2.5, fill: color }} activeDot={{ r: 4 }} style={{ filter: `drop-shadow(0 0 6px ${color}77)` }} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export function NoteWheel({ data, color = "#8B5CF6" }) {
  return (
    <div data-testid="viz-note-wheel">
      <ResponsiveContainer width="100%" height={260}>
        <RadarChart data={data} outerRadius="72%">
          <PolarGrid stroke="rgba(255,255,255,0.08)" />
          <PolarAngleAxis dataKey="note" tick={{ fill: "#94A3B8", fontSize: 11, fontFamily: "JetBrains Mono, monospace" }} />
          <Radar dataKey="value" stroke={color} strokeWidth={2} fill={color} fillOpacity={0.3} style={{ filter: `drop-shadow(0 0 6px ${color}66)` }} />
          <Tooltip contentStyle={TT} />
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function CategoryRadar({ data, color = "#22D3EE" }) {
  return (
    <div data-testid="viz-category-radar">
      <ResponsiveContainer width="100%" height={260}>
        <RadarChart data={data} outerRadius="70%">
          <PolarGrid stroke="rgba(255,255,255,0.08)" />
          <PolarAngleAxis dataKey="name" tick={{ fill: "#94A3B8", fontSize: 11 }} />
          <Radar dataKey="score" stroke={color} strokeWidth={2} fill={color} fillOpacity={0.3} style={{ filter: `drop-shadow(0 0 6px ${color}66)` }} />
          <Tooltip contentStyle={TT} />
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function StatusDonut({ breakdown }) {
  const data = Object.entries(breakdown || {}).filter(([, v]) => v > 0).map(([k, v]) => ({ name: k, value: v }));
  if (!data.length) return null;
  return (
    <div data-testid="viz-status-donut">
      <ResponsiveContainer width="100%" height={200}>
        <PieChart>
          <Pie data={data} dataKey="value" nameKey="name" innerRadius={52} outerRadius={78} paddingAngle={3} stroke="none">
            {data.map((d, i) => <Cell key={i} fill={vizColor(d.name)} style={{ filter: `drop-shadow(0 0 5px ${vizColor(d.name)}66)` }} />)}
          </Pie>
          <Legend formatter={(val) => <span style={{ color: "#94A3B8", fontSize: 11 }}>{val.charAt(0) + val.slice(1).toLowerCase()}</span>} />
          <Tooltip contentStyle={TT} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

export function StrengthBars({ rows, nameKey = "name" }) {
  return (
    <div className="space-y-2.5" data-testid="viz-strength-bars">
      {rows.map((r, i) => {
        const c = r.status ? vizColor(r.status) : scoreColor(r.score);
        return (
          <div key={r[nameKey] || i}>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-slate-200">{r[nameKey]}</span>
              <span style={{ color: c, fontFamily: "JetBrains Mono, monospace" }}>{r.score}%</span>
            </div>
            <div className="h-2 rounded-full bg-white/[0.05] overflow-hidden">
              <motion.div className="h-full rounded-full" style={{ background: `linear-gradient(90deg, ${c}99, ${c})`, boxShadow: `0 0 8px ${c}88` }}
                initial={{ width: 0 }} whileInView={{ width: `${r.score}%` }} viewport={{ once: true }} transition={{ duration: 0.9, delay: i * 0.03 }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}
